import { z } from 'zod';
import { apiGet, apiGetBlob, apiPost } from '../../lib/api';

/**
 * Semnarea, dinspre operator — **atârnată de document**.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/signing`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. Granița se validează în ambele capete.
 *
 * ⚠️ **Nu mai există sesiuni, nici în interfață, nici în bază** (#115). Semnarea e 1:1 cu
 * actul, deci fiecare rută atârnă de `documentId`. Nu mai există nici o listă de sesiuni:
 * starea pe care o citește operatorul e a **documentului**, iar două vocabulare pentru
 * aceeași hârtie ar fi fost două adevăruri.
 */

export const signingSchema = z.object({
  documentId: z.uuid(),
  /**
   * `null` pe actele plecate la semnat ca ciornă, între #154 și #221. Aceeași greșeală ca în
   * `public-signing.api.ts` stătea și aici — vezi acolo.
   */
  documentNumber: z.number().int().nullable(),
  documentDate: z.string(),
  documentTypeName: z.string(),
  customerId: z.uuid(),
  customerName: z.string(),
  companyName: z.string(),
  sentToEmail: z.string().nullable(),
  expiresAt: z.string(),
  sentAt: z.string().nullable(),
  openedAt: z.string().nullable(),
  signedAt: z.string().nullable(),
  signerName: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});

export type DocumentSigning = z.infer<typeof signingSchema>;

/**
 * Trimiterea la semnat.
 *
 * `email` gol înseamnă „adresa de pe fișă". Se trimite explicit doar când semnatarul are
 * altă cutie poștală decât cea de facturare — cazul obișnuit la persoane juridice.
 */
export function sendToSigning(
  documentId: string,
  input: {
    email?: string;
    /** `'link'` nu trimite niciun email: linkul se copiază și pleacă pe WhatsApp. */
    delivery?: 'email' | 'link';
  },
): Promise<DocumentSigning> {
  return apiPost(
    `/documents/${documentId}/signing`,
    { email: input.email ?? '', delivery: input.delivery ?? 'email' },
    z.object({ signing: signingSchema }),
  ).then((body) => body.signing);
}

/**
 * Invalidarea. **Documentul se întoarce în `1`, cu numărul păstrat** (#221).
 *
 * E primul pas obligatoriu ori de câte ori clientul vrea altceva în contract: invalidezi,
 * generezi document nou, iar pe cel vechi îl anulezi — are număr, deci nu se mai șterge.
 */
export function discardSigning(documentId: string): Promise<void> {
  return apiPost(
    `/documents/${documentId}/signing/cancel`,
    {},
    z.object({ ok: z.literal(true) }),
  ).then(() => undefined);
}

const signingLinkSchema = z.object({
  url: z.url(),
  expiresAt: z.string(),
});

export type SigningLink = z.infer<typeof signingLinkSchema>;

/**
 * Linkul, ca operatorul să-l trimită pe WhatsApp.
 *
 * ⚠️ **Nu se pune în cache și nu are `queryOptions`.** E cerut la apăsarea butonului, nu la
 * deschiderea ecranului: linkul e tokenul în clar, iar un cache de React Query l-ar ține în
 * memoria filei mult după ce operatorul a terminat cu el. Fiecare apăsare lasă un rând în
 * jurnal — o citire de fundal ar fi scris rânduri pe care nu le-a cerut nimeni.
 */
export function fetchSigningLink(documentId: string): Promise<SigningLink> {
  return apiGet(`/documents/${documentId}/signing/link`, signingLinkSchema);
}

/**
 * Actul semnat, ca PDF — **fișierul salvat la semnare**.
 *
 * ⚠️ **Nu e același lucru cu „Vezi PDF" din registru.** Acela randează versiunea înghețată,
 * care nu conține semnătura: semnătura nu e în textul documentului. Aici vine exact ce a
 * semnat clientul, din storage.
 */
export function fetchSignedDocumentFile(documentId: string): Promise<Blob> {
  return apiGetBlob(`/documents/${documentId}/signing/pdf`);
}

/**
 * Același fișier, deschis **într-o filă nouă**.
 *
 * Nu un `<a href>` simplu: ruta cere cookie-ul de sesiune și poate răspunde cu 403
 * (`signing.view`) sau 404, iar un link mort s-ar fi văzut ca o filă goală.
 */
export async function openSignedDocumentPdf(documentId: string): Promise<void> {
  const blob = await fetchSignedDocumentFile(documentId);
  const url = URL.createObjectURL(blob);

  window.open(url, '_blank', 'noopener');
  // Revocarea imediată ar goli fereastra înainte să apuce s-o citească.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
